package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initNativeUnderLoadPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/NativeUnderLoad.md", FrontMatterElement.Builder { addScalar("title", "Kotlin/Native under load, and what I measured wrong"); addScalar("searchTitle", "Kotlin/Native vs JVM under load — startup, memory and p99"); addScalar("date", "2026-08-12"); addScalar("description", "28 ms to first response and 20 MiB idle. Then the load arrived — and three of my own measurements turned out to be wrong."); }))
}

@Page("/blog/native-under-load")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun NativeUnderLoadPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two services of mine run on Kotlin/Native, and the numbers that got them there are real:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("JVM")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Kotlin/Native")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Time to first 200")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2 801 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("28 ms")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Idle memory")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("137 MiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("20 MiB")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A hundredfold faster start in a seventh of the memory. The question was whether that holds once traffic arrives. It does not, and the reason is not the one everybody names.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-load-does-to-it") }) {
        org.jetbrains.compose.web.dom.Text("What load does to it")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Same code, single core:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("JVM")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Kotlin/Native")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("p99 @ 2000 rps, no database")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2.54 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("11.55 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("p99 @ 2000 rps, with a read")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("60.6 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("1 360 ms")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Memory under load")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("189 MiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("688 MiB")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("p99 spread between identical runs")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1.35×")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("109×")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The last row is the one that decides it. A number three times worse is a number you can plan around. A hundredfold spread between identical runs is not.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("it-is-not-the-garbage-collector") }) {
        org.jetbrains.compose.web.dom.Text("It is not the garbage collector")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That was my first assumption and it was wrong. Garbage collection took ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("1.4 %")
        }
        org.jetbrains.compose.web.dom.Text(" of CPU.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The profile:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("share")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Kernel and runtime")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("41.3 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Ktor")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("27.0 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Other Kotlin")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("13.7 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Memory allocator")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8.5 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Garbage collection")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1.4 %")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Memory does not grow because collecting is expensive. It grows because ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("allocating")
        }
        org.jetbrains.compose.web.dom.Text(" is heavy and a full-heap collection is too expensive to run often enough to keep up.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The single largest entry was ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SelectorHelper.selectionLoop")
        }
        org.jetbrains.compose.web.dom.Text(" at 17.1 % — Ktor's network layer on Linux ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("poll")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The clincher was comparing two ingest paths in the same binary at comparable rates: UDP sat at 35 MiB, HTTP at 350 MiB. Tenfold, same GC, same allocator. A minimal Ktor server with one trivial route allocates roughly ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("13 KB of garbage per request")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("three-things-i-measured-wrong") }) {
        org.jetbrains.compose.web.dom.Text("Three things I measured wrong")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is the part I would want to read.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The load generator shared the machine with the service.")
        }
        org.jetbrains.compose.web.dom.Text(" It was competing for the same cores. Moving it to another host raised the ceiling by 400 %. Every number taken before that was a measurement of the generator.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The generator deduplicated on the wrong key.")
        }
        org.jetbrains.compose.web.dom.Text(" It used the window timestamp instead of the sequence number, so 88 % of frames were discarded as duplicates. What I read as throughput was timestamp diversity in my own test harness.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("I tuned an allocator that no longer exists.")
        }
        org.jetbrains.compose.web.dom.Text(" I set environment variables for an allocator removed from Kotlin/Native in 2.2, saw the numbers move, and wrote it up as the effect of tuning. Run-to-run spread on that setup was 5–11×. The variables did nothing; I was reading noise and giving it a cause.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("generational-collection-tested-rather-than-assumed") }) {
        org.jetbrains.compose.web.dom.Text("Generational collection, tested rather than assumed")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If allocation is the problem, would collecting more often help? I forced it, at intervals, to find out.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Collecting every 50 ms pushed CPU from 55 % to 86 % and p99 from 0.75 ms to 5.75 ms. So no — not like that.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("But it clarified what generational collection actually buys:")
    }
    com.varabyte.kobweb.compose.dom.GenericTag("blockquote") {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("Its value is not that collection is cheap. It is that ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("frequent")
            }
            org.jetbrains.compose.web.dom.Text(" collection becomes cheap, so the high-water mark never grows.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The JVM held 2000 rps in 190 MiB total with a 64 MiB heap. That is the mechanism doing the work, and Kotlin/Native does not have it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("rules-i-now-follow") }) {
        org.jetbrains.compose.web.dom.Text("Rules I now follow")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Put the load generator on a different machine.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Warm up with the profile you are about to measure, not a different one.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Six repetitions minimum, with the order of variants randomised.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Report the median ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("and the spread")
            }
            org.jetbrains.compose.web.dom.Text(". Any effect smaller than the spread is not an effect.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Read anonymous memory from ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("memory.stat")
            }
            org.jetbrains.compose.web.dom.Text(", not ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("memory.current")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Checksum the binary to prove a build flag changed anything at all.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-it-still-wins") }) {
        org.jetbrains.compose.web.dom.Text("Where it still wins")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Not sustained HTTP under a memory limit. But the 20 MiB idle footprint and the hundredfold faster start are real, and they are exactly what short-lived processes want: CLI tools, agents, cron jobs, webhook receivers, scale-to-zero containers.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Both services went back to the JVM, keeping the shared code through Kotlin Multiplatform — which is the arrangement KMP is for.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The next thing worth fixing is not a garbage collector. It is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("poll")
        }
        org.jetbrains.compose.web.dom.Text(" in Ktor's network layer, which on Linux should be ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("epoll")
        }
        org.jetbrains.compose.web.dom.Text(". That is a library-level change, and a much smaller one than it sounds.")
    }
}
